// Built-in product icons are uniformly monochrome SVG (no emoji/punctuation).
// Style: stroke=currentColor, round caps, inherits the text color.

interface IconProps { size?: number; }

const base = (size: number) => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const, 'aria-hidden': true,
});

export function PencilIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

export function XIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** Alert (used in error toasts): triangle with exclamation mark, monochrome */
export function AlertIcon({ size = 14 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

/** Collapse/expand (both side panels are collapsible) */
export function ChevronLeftIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}
export function ChevronRightIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/** Multi-select alignment: baseline + two offset blocks, the industry-standard glyph */
export function AlignLeftIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M4 3v18" />
      <rect x="7" y="6" width="13" height="4" rx="1" />
      <rect x="7" y="14" width="8" height="4" rx="1" />
    </svg>
  );
}
export function AlignHCenterIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M12 3v18" />
      <rect x="5" y="6" width="14" height="4" rx="1" />
      <rect x="8" y="14" width="8" height="4" rx="1" />
    </svg>
  );
}
export function AlignRightIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M20 3v18" />
      <rect x="4" y="6" width="13" height="4" rx="1" />
      <rect x="9" y="14" width="8" height="4" rx="1" />
    </svg>
  );
}
export function AlignTopIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M3 4h18" />
      <rect x="6" y="7" width="4" height="13" rx="1" />
      <rect x="14" y="7" width="4" height="8" rx="1" />
    </svg>
  );
}
export function AlignVCenterIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M3 12h18" />
      <rect x="6" y="5" width="4" height="14" rx="1" />
      <rect x="14" y="8" width="4" height="8" rx="1" />
    </svg>
  );
}
export function AlignBottomIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      <path d="M3 20h18" />
      <rect x="6" y="4" width="4" height="13" rx="1" />
      <rect x="14" y="9" width="4" height="8" rx="1" />
    </svg>
  );
}

export function CheckIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function PlusIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function ArrowUpIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 19V5m-7 7 7-7 7 7" />
    </svg>
  );
}

export function ArrowDownIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M12 5v14m7-7-7 7-7-7" />
    </svg>
  );
}

/** Preview mode (play/stop) */
export function PlayIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M7 4.5v15l13-7.5Z" />
    </svg>
  );
}

export function StopIcon({ size = 11 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 10 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function CropIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)}>
      <rect x="7" y="7" width="13" height="13" rx="1" strokeDasharray="3 2.4" />
      <path d="M4 4h3M4 4v3" />
    </svg>
  );
}

// Mobile bottom-nav icons
export function ChatIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export function FrameIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}

export function LayersIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="m12 2 8.5 4.5L12 11 3.5 6.5 12 2z" />
      <path d="m3.5 12 8.5 4.5L20.5 12" />
      <path d="m3.5 17.5 8.5 4.5 8.5-4.5" />
    </svg>
  );
}

// Layer-list icons (replace the text glyphs ▢/T)
export function ImageObjIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}

export function TextObjIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M4 7V4h16v3M12 4v16M9 20h6" />
    </svg>
  );
}

export function RectObjIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <rect x="3" y="5" width="18" height="14" rx="3" />
    </svg>
  );
}
/** SPEC-453 M1: frame layer icon — a container glyph, distinct from the plain rect */
export function FrameObjIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 9v12" />
    </svg>
  );
}

/* SPEC-457 M1: icon 对象（单色矢量图标）的图层面板角标 — lucide star 同一 path 风格。 */
export function IconObjIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    </svg>
  );
}

/** Undo: left curved arrow, monochrome */
export function UndoIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  );
}

/** Redo: right curved arrow, monochrome */
export function RedoIcon({ size = 13 }: IconProps) {
  return (
    <svg {...base(size)} strokeWidth={2}>
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
    </svg>
  );
}

/** Collapse (props panel) */
export function ChevronUpIcon({ size = 10 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M6 15l6-6 6 6" />
    </svg>
  );
}

/** Home (back to project gallery — SPEC-404) */
export function HomeIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}

/** Eye — layer shown (SPEC-405 layer visibility toggle) */
export function EyeIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Eye with slash — layer hidden (SPEC-405 layer visibility toggle) */
export function EyeOffIcon({ size = 12 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

/** Hamburger — topbar overflow-menu toggle (SPEC-468 M1 mobile shell) */
export function MenuIcon({ size = 16 }: IconProps) {
  return (
    <svg {...base(size)}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
